import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle, AlertCircle, Eye, EyeOff, ArrowLeft, Send } from "lucide-react";
import axios from "axios";
import Cookies from "js-cookie";
import { getClientAuthHeaders, getClientAuthToken } from "@/lib/apiAuth";
import Sidebar from "@/components/Sidebar";

const TelegramBotIntegration = () => {
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Form state
  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);

  // Integration state
  const [integrationId, setIntegrationId] = useState(null);
  const [integrationStatus, setIntegrationStatus] = useState(null); // active, error, pending_auth, paused
  const [isEnabled, setIsEnabled] = useState(true);
  const [hasExistingIntegration, setHasExistingIntegration] = useState(false);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [apiError, setApiError] = useState("");
  const [success, setSuccess] = useState(false);
  const [statusNotification, setStatusNotification] = useState(null);

  // Load existing integration on mount
  useEffect(() => {
    loadExistingIntegration();
  }, []);

  const loadExistingIntegration = async () => {
    try {
      const authToken = getClientAuthToken();
      if (!authToken) {
        navigate("/login");
        return;
      }

      const response = await axios.get(
        `${BACKEND_URL}/api/integrations/telegram`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const data = response.data;

      if (data.has_integration && data.integration) {
        const integration = data.integration;
        setIntegrationId(integration.id);
        setIntegrationStatus(integration.status);
        setIsEnabled(integration.status !== "paused");
        setHasExistingIntegration(true);

        // Populate form with existing credentials
        if (integration.credentials) {
          setToken(integration.credentials.token || "");
        }
      }
    } catch (err) {
      console.error("Error loading integration:", err);
      if (err.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field);
  };

  const validateField = (field) => {
    const newErrors = { ...errors };

    switch (field) {
      case "token":
        if (!token.trim()) {
          newErrors.token = "Token is required";
        } else {
          delete newErrors.token;
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateAllFields = () => {
    const newErrors = {};

    if (!token.trim()) {
      newErrors.token = "Token is required";
    }

    setErrors(newErrors);
    setTouched({
      token: true,
    });

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateAllFields()) {
      return;
    }

    setIsSaving(true);
    setApiError("");
    setSuccess(false);

    try {
      const authToken = getClientAuthToken();
      if (!authToken) {
        setApiError("You are not logged in. Please login and try again.");
        setIsSaving(false);
        return;
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/integrations/save`,
        {
          platform: "telegram",
          category: "messaging",
          credentials: {
            token: token,
          },
          settings: {
            is_primary_channel: false,
            sync_frequency: "realtime",
          },
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const data = response.data;

      if (data.status === "ok") {
        setSuccess(true);
        setIntegrationId(data.integration_id);
        setIntegrationStatus("active");
        setIsEnabled(true);
        setHasExistingIntegration(true);

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setApiError(data.message || "Failed to save integration. Please try again.");
      }
    } catch (err) {
      console.error("Save integration error:", err);

      if (err.response) {
        if (err.response.status === 401) {
          setApiError("Session expired. Please login again.");
          setTimeout(() => navigate("/login"), 2000);
        } else {
          const errorMessage =
            err.response.data?.detail ||
            err.response.data?.message ||
            "Failed to save integration. Please try again.";
          setApiError(errorMessage);
        }
      } else if (err.request) {
        setApiError("Network error. Please check your connection and try again.");
      } else {
        setApiError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!hasExistingIntegration) return;

    setIsTogglingStatus(true);
    setApiError("");
    setStatusNotification(null);

    try {
      const authToken = getClientAuthToken();
      if (!authToken) {
        setApiError("You are not logged in. Please login and try again.");
        setIsTogglingStatus(false);
        return;
      }

      const newStatus = isEnabled ? "paused" : "active";

      const response = await axios.put(
        `${BACKEND_URL}/api/integrations/telegram/status?status=${newStatus}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const data = response.data;

      if (data.status === "ok") {
        setIsEnabled(!isEnabled);
        setIntegrationStatus(newStatus);

        // Show success notification
        const statusLabel = newStatus === "active" ? "enabled" : "disabled";
        setStatusNotification({
          type: "success",
          message: `Integration has been ${statusLabel} successfully`,
        });

        // Clear notification after 3 seconds
        setTimeout(() => setStatusNotification(null), 3000);
      } else {
        setStatusNotification({
          type: "error",
          message: data.message || "Failed to update integration status.",
        });
        setTimeout(() => setStatusNotification(null), 5000);
      }
    } catch (err) {
      console.error("Toggle status error:", err);

      let errorMessage = "Failed to update integration status.";

      if (err.response) {
        if (err.response.status === 401) {
          setApiError("Session expired. Please login again.");
          setTimeout(() => navigate("/login"), 2000);
          return;
        } else {
          errorMessage =
            err.response.data?.detail ||
            err.response.data?.message ||
            "Failed to update integration status.";
        }
      } else {
        errorMessage = "Network error. Please try again.";
      }

      setStatusNotification({
        type: "error",
        message: errorMessage,
      });
      setTimeout(() => setStatusNotification(null), 5000);
    } finally {
      setIsTogglingStatus(false);
    }
  };

  const isFormValid = token.trim().length > 0;

  const getStatusBadge = () => {
    if (!hasExistingIntegration) return null;

    const statusConfig = {
      active: {
        bg: "bg-green-500/10",
        border: "border-green-500/30",
        text: "text-green-400",
        label: "Active",
        icon: CheckCircle,
      },
      paused: {
        bg: "bg-yellow-500/10",
        border: "border-yellow-500/30",
        text: "text-yellow-400",
        label: "Paused",
        icon: AlertCircle,
      },
      error: {
        bg: "bg-red-500/10",
        border: "border-red-500/30",
        text: "text-red-400",
        label: "Error",
        icon: AlertCircle,
      },
      pending_auth: {
        bg: "bg-blue-500/10",
        border: "border-blue-500/30",
        text: "text-blue-400",
        label: "Pending Auth",
        icon: AlertCircle,
      },
    };

    const config = statusConfig[integrationStatus] || statusConfig.pending_auth;
    const Icon = config.icon;

    return (
      <div
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${config.bg} ${config.border} border`}
      >
        <Icon className={`w-4 h-4 ${config.text}`} />
        <span className={`text-sm font-medium ${config.text}`}>{config.label}</span>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0F1115] flex" data-testid="telegram-integration-page">
        <Sidebar />
        <div className="flex-1 md:ml-64 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00D1FF]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1115] flex" data-testid="telegram-integration-page">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content Container */}
      <div className="flex-1 md:ml-64 flex flex-col">
        {/* Main Content */}
        <main className="flex-1 px-6 py-10">
          <div className="max-w-2xl mx-auto animate-fade-in-up">
            {/* Back Button */}
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-body">Back to Dashboard</span>
            </button>

            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-[#229ED9]/10 flex items-center justify-center">
                  <Send className="w-5 h-5 text-[#229ED9]" />
                </div>
                <h1 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6]">
                  Telegram Bot Integration
                </h1>
              </div>
              <p className="text-[#9CA3AF] font-body text-sm">
                Connect your Telegram Bot to enable AI-powered messaging
              </p>
            </div>

            {/* Integration Status Card */}
            {hasExistingIntegration && (
              <div className="glass rounded-2xl p-6 border border-white/10 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-[#F3F4F6] font-body text-sm">Current Status:</span>
                    {getStatusBadge()}
                  </div>

                  {/* Enable/Disable Toggle */}
                  <div className="flex items-center gap-3">
                    <span className="text-[#9CA3AF] text-sm font-body">
                      {isEnabled ? "Enabled" : "Disabled"}
                    </span>
                    <button
                      onClick={handleToggleStatus}
                      disabled={isTogglingStatus}
                      className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${
                        isEnabled ? "bg-[#00D1FF]" : "bg-white/20"
                      } ${isTogglingStatus ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                      data-testid="status-toggle"
                    >
                      <div
                        className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform duration-300 ${
                          isEnabled ? "translate-x-7" : "translate-x-1"
                        }`}
                      />
                      {isTogglingStatus && (
                        <Loader2 className="w-3 h-3 animate-spin absolute top-1.5 left-1/2 -translate-x-1/2 text-[#0F1115]" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Status Change Notification */}
                {statusNotification && (
                  <div
                    className={`mt-4 p-3 rounded-lg flex items-center gap-2 animate-fade-in-up ${
                      statusNotification.type === "success"
                        ? "bg-green-500/10 border border-green-500/30"
                        : "bg-red-500/10 border border-red-500/30"
                    }`}
                    data-testid="status-notification"
                  >
                    {statusNotification.type === "success" ? (
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                    )}
                    <span
                      className={`text-sm font-body ${
                        statusNotification.type === "success" ? "text-green-400" : "text-red-400"
                      }`}
                    >
                      {statusNotification.message}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Configuration Form Card */}
            <div className="glass rounded-2xl p-8 md:p-10 border border-white/10">
              {/* Section Header */}
              <div className="mb-8">
                <h2 className="font-heading font-bold text-lg md:text-xl text-[#F3F4F6] mb-2">
                  {hasExistingIntegration ? "Update Configuration" : "Connection Settings"}
                </h2>
                <p className="text-[#9CA3AF] font-body text-sm">
                  Enter your Telegram Bot API credentials
                </p>
              </div>

              {/* Success Message */}
              {success && (
                <div
                  className="mb-6 p-4 rounded-lg bg-green-500/10 border border-green-500/30"
                  data-testid="success-message"
                >
                  <p className="text-green-400 text-sm font-body text-center flex items-center justify-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    {hasExistingIntegration
                      ? "Integration updated successfully!"
                      : "Integration saved successfully!"}
                  </p>
                </div>
              )}

              {/* API Error */}
              {apiError && (
                <div
                  className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30"
                  data-testid="api-error"
                >
                  <p className="text-red-400 text-sm font-body text-center">{apiError}</p>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Token Input */}
                <div className="space-y-2">
                  <label
                    htmlFor="token-input"
                    className="block text-[#F3F4F6] text-sm font-body"
                  >
                    Token <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="token-input"
                      type={showToken ? "text" : "password"}
                      value={token}
                      onChange={(e) => {
                        setToken(e.target.value);
                        setApiError("");
                        setSuccess(false);
                        if (touched.token) validateField("token");
                      }}
                      onBlur={() => handleBlur("token")}
                      placeholder="123456789:ABCDefGhIJKlmNoPQRsTUVwxyZ"
                      disabled={isSaving}
                      className={`w-full bg-white/5 border rounded-lg px-4 py-3 pr-12 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none transition-all font-body disabled:opacity-50 ${
                        errors.token && touched.token
                          ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
                          : "border-white/10 focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50"
                      }`}
                      data-testid="token-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowToken(!showToken)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors"
                      tabIndex={-1}
                    >
                      {showToken ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  {errors.token && touched.token && (
                    <p className="text-red-400 text-xs font-body">{errors.token}</p>
                  )}
                  <p className="text-[#9CA3AF]/70 text-xs font-body">
                    Bot token obtained from @BotFather on Telegram
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={!isFormValid || isSaving}
                  className={`w-full font-heading font-bold text-base md:text-lg py-4 px-6 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2 mt-8 ${
                    isFormValid && !isSaving
                      ? "btn-pulse bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
                      : "bg-[#00D1FF]/50 text-[#0F1115]/70 cursor-not-allowed"
                  }`}
                  data-testid="save-button"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      {hasExistingIntegration ? "Update Configuration" : "Save Configuration"}
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Help Section */}
            <div className="mt-6 p-4 rounded-lg bg-white/5 border border-white/10">
              <h3 className="font-heading font-semibold text-sm text-[#F3F4F6] mb-2">
                Need help finding your bot token?
              </h3>
              <p className="text-[#9CA3AF] text-xs font-body leading-relaxed">
                Open Telegram, search for{" "}
                <a
                  href="https://t.me/BotFather"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#00D1FF] hover:underline"
                >
                  @BotFather
                </a>
                , and send <span className="text-[#F3F4F6] font-mono">/newbot</span> to create a new bot or{" "}
                <span className="text-[#F3F4F6] font-mono">/token</span> to get the token for an existing bot.
              </p>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-6 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-6 mb-4">
              <a
                href="https://fractolabs.com/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#9CA3AF] text-sm hover:text-[#F3F4F6] transition-colors"
              >
                Privacy Policy
              </a>
              <span className="text-[#9CA3AF]/30">|</span>
              <a
                href="https://fractolabs.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#9CA3AF] text-sm hover:text-[#F3F4F6] transition-colors"
              >
                Terms of Service
              </a>
            </div>
            <p className="text-[#9CA3AF]/60 text-xs font-mono">
              © 2026 FractoLabs. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default TelegramBotIntegration;
